import { memo, type ReactNode } from "react";
import { cn } from "@/ui/kit/cn";
import { w } from "./vars";

const W = (key: string) => `weechat.${key}`;

function Wc({
  k,
  bg,
  children,
  bold,
}: {
  k?: string;
  bg?: string;
  children: ReactNode;
  bold?: boolean;
}) {
  return (
    <span
      data-token={k ? W(k) : bg ? W(bg) : undefined}
      style={{
        color: k ? w(k) : undefined,
        background: bg ? w(bg) : undefined,
        fontWeight: bold ? 700 : undefined,
      }}
    >
      {children}
    </span>
  );
}

const NICKS: [string, number][] = [
  ["lnu", 0],
  ["folke", 1],
  ["mitchellh", 2],
  ["tpope", 3],
  ["echasnovski", 4],
  ["justinmk", 5],
];

function Msg({ time, prefix, children }: { time: string; prefix: ReactNode; children: ReactNode }) {
  return (
    <div className="flex whitespace-pre">
      <Wc k="weechat.color:chat_time">{time}</Wc>
      <span className="inline-block w-[13ch] text-right">{prefix}</span>
      <Wc k="weechat.color:chat_prefix_suffix"> │ </Wc>
      <span className="min-w-0 flex-1 truncate">{children}</span>
    </div>
  );
}

const nick = (name: string, i: number) => <Wc k={`nick_color_${(i % 12) + 1}`}>{name}</Wc>;

export const WeeChatPreview = memo(function WeeChatPreview({
  compact = false,
}: {
  compact?: boolean;
}) {
  const buffers: [string, string, string?][] = [
    ["1", "weechat"],
    ["2", "libera"],
    ["3", "#neovim", "buflist:hotlist_message"],
    ["4", "#herdr"],
    ["5", "folke", "buflist:hotlist_private"],
    ["6", "#ghostty", "buflist:hotlist_highlight"],
  ];
  return (
    <div
      className={cn(
        "flex h-full flex-col",
        compact ? "text-[11px] leading-[1.45]" : "text-[13px] leading-[1.55]",
      )}
      style={{ background: w("weechat.color:chat_bg"), color: w("weechat.color:chat") }}
      data-token="weechat.weechat.color:chat_bg"
    >
      <div
        className="truncate whitespace-pre px-[1ch]"
        style={{
          background: w("weechat.bar:title.color_bg"),
          color: w("weechat.bar:title.color_fg"),
        }}
        data-token="weechat.weechat.bar:title.color_bg"
      >
        Neovim: hyperextensible Vim-based text editor | https://neovim.io
      </div>
      <div className="flex min-h-0 flex-1">
        <div
          className={cn("shrink-0 border-r", compact ? "w-[13ch]" : "w-[16ch]")}
          style={{
            background: w("weechat.bar:buflist.color_bg"),
            borderColor: w("weechat.color:separator"),
          }}
        >
          {buffers.map(([n, name, hot]) => {
            const current = name === "#neovim";
            return (
              <div
                key={n}
                className="truncate whitespace-pre"
                style={{ background: current ? w("buflist:current_bg") : undefined }}
                data-token={current ? "weechat.buflist:current_bg" : undefined}
              >
                <Wc k="weechat.color:chat_prefix_buffer">{n}.</Wc>
                <Wc k={hot ?? "buflist:hotlist_low"}>
                  {name.startsWith("#") || n === "5" ? " " : ""}
                  {name}
                </Wc>
              </div>
            );
          })}
        </div>
        <div className="min-w-0 flex-1 px-[1ch]">
          <Msg time="14:01" prefix={<Wc k="weechat.color:chat_prefix_join">--&gt;</Wc>}>
            {nick("tpope", 3)} <Wc k="weechat.color:chat_delimiters">(</Wc>
            <Wc k="weechat.color:chat_host">~tpope@vim/tpope</Wc>
            <Wc k="weechat.color:chat_delimiters">)</Wc>{" "}
            <Wc k="irc.color:message_join">has joined</Wc>{" "}
            <Wc k="weechat.color:chat_channel">#neovim</Wc>
          </Msg>
          <Msg time="14:02" prefix={nick("folke", 1)}>
            is the new palette on github yet?
          </Msg>
          <Msg time="14:02" prefix={nick("mitchellh", 2)}>
            ghostty picks it up from the theme file
          </Msg>
          <Msg
            time="14:03"
            prefix={
              <Wc k="weechat.color:chat_nick_self" bold>
                lnu
              </Wc>
            }
          >
            exported it from themes, one prompt per app
          </Msg>
          <Msg
            time="14:03"
            prefix={
              <Wc k="weechat.color:chat_highlight" bold>
                echasnovski
              </Wc>
            }
          >
            <Wc k="weechat.color:chat_highlight">lnu: mini.icons colors look right too</Wc>
          </Msg>
          <Msg time="14:04" prefix={<Wc k="weechat.color:chat_prefix_action">*</Wc>}>
            {nick("justinmk", 5)} adds it to the colorscheme list
          </Msg>
          {!compact && (
            <>
              <div
                className="whitespace-pre"
                style={{ color: w("weechat.color:chat_read_marker") }}
                data-token="weechat.weechat.color:chat_read_marker"
              >
                {"- ".repeat(40)}
              </div>
              <Msg time="14:06" prefix={<Wc k="weechat.color:chat_prefix_network">--</Wc>}>
                <Wc k="irc.color:notice">Notice</Wc>(<Wc k="weechat.color:chat_server">libera</Wc>):
                connection secured
              </Msg>
              <Msg time="14:07" prefix={<Wc k="weechat.color:chat_prefix_quit">&lt;--</Wc>}>
                {nick("tpope", 3)} <Wc k="irc.color:message_quit">has quit</Wc>{" "}
                <Wc k="irc.color:reason_quit">(Quit: :wq)</Wc>
              </Msg>
              <Msg time="14:08" prefix={<Wc k="weechat.color:chat_prefix_error">=!=</Wc>}>
                <Wc k="weechat.color:chat_prefix_error">irc: unknown command "/thme"</Wc>
              </Msg>
              <Msg time="14:09" prefix={nick("echasnovski", 4)}>
                <Wc k="weechat.color:chat_inactive_window">
                  every nick gets a color from chat_nick_colors
                </Wc>
              </Msg>
            </>
          )}
        </div>
        {!compact && (
          <div
            className="w-[14ch] shrink-0 border-l px-[1ch]"
            style={{
              background: w("weechat.bar:nicklist.color_bg"),
              borderColor: w("weechat.color:separator"),
            }}
          >
            <div className="whitespace-pre">
              <Wc k="weechat.color:nicklist_group">@</Wc>
              {nick("justinmk", 5)}
            </div>
            <div className="whitespace-pre">
              <Wc k="weechat.color:status_number">+</Wc>
              {nick("folke", 1)}
            </div>
            {NICKS.slice(2, 5).map(([n, i]) => (
              <div key={n} className="whitespace-pre">
                {" "}
                {nick(n, i)}
              </div>
            ))}
            <div className="whitespace-pre">
              {" "}
              <Wc k="weechat.color:nicklist_away">tpope</Wc>
            </div>
          </div>
        )}
      </div>
      <div
        className="flex whitespace-pre px-[1ch]"
        style={{
          background: w("weechat.bar:status.color_bg"),
          color: w("weechat.bar:status.color_fg"),
        }}
        data-token="weechat.weechat.bar:status.color_bg"
      >
        <Wc k="weechat.bar:status.color_delim">[</Wc>
        <Wc k="weechat.color:status_time">14:09</Wc>
        <Wc k="weechat.bar:status.color_delim">] [</Wc>
        <Wc k="weechat.color:status_number">3</Wc>
        <Wc k="weechat.bar:status.color_delim">]</Wc> <Wc k="weechat.color:status_name">#neovim</Wc>
        <Wc k="weechat.bar:status.color_delim">{"{"}</Wc>42
        <Wc k="weechat.bar:status.color_delim">{"}"}</Wc>{" "}
        <Wc k="weechat.bar:status.color_delim">[Act: </Wc>
        <Wc k="weechat.color:status_data_highlight">6</Wc>,
        <Wc k="weechat.color:status_data_private">5</Wc>,
        <Wc k="weechat.color:status_data_msg">4</Wc>
        <Wc k="weechat.bar:status.color_delim">]</Wc>
      </div>
      <div
        className="whitespace-pre px-[1ch]"
        style={{
          background: w("weechat.bar:input.color_bg"),
          color: w("weechat.bar:input.color_fg"),
        }}
      >
        [<Wc k="irc.color:input_nick">lnu</Wc>] /theme
        <span style={{ background: w("weechat.color:chat") }}> </span>
      </div>
    </div>
  );
});
